import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Skeleton, Tabs, Tab, Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import Loader from "shared/components/loader/animation";
import { toastMessage } from "shared/components/toast";
import Confirmation from "shared/modals/confirmation";
import CropImage from "shared/modals/cropImage";
import {
  addBanner,
  deleteBanner,
  getBannerListings,
} from "shared/services/productManagerService";
import styles from "./style.module.scss";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";

import { routeConstant } from "shared/routes/routesConstant";

interface Image {
  url: string;
}

/** Every banner is cropped to its screen's size before it is uploaded. */
const BANNER_SIZES = {
  home: { width: 1456, height: 560 },
  welcome: { width: 1284, height: 1419 },
};

const Banners: React.FC = () => {
  const [allImages, setAllImages] = useState<Image[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [selectedPicture, setSelectedPicture] = useState<any>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [allBanners, setAllBanners] = useState<any>([]);
  const [activeTab, setActiveTab] = useState<number>(0);

  const getAllBanners = (loader: any, isMobile: boolean = false) => {
    if (loader) {
      setLoader(true);
    }
    getBannerListings(isMobile ? "true" : "false")
      .then(({ data: { data } }) => {
        setLoader(false);
        setAllBanners(Array.isArray(data) ? data : data?.data ?? []);
      })
      .catch((err) => {
        setLoader(false);
        toastMessage("error", err?.message);
      });
  };

  const handleClickOpen = (item: Image) => {
    setOpen(true);
    setSelectedPicture(item);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPicture(null);
  };

  const handleImageUpload = (newImage: Image) => {
    setAllImages((prevImages) => [...prevImages, newImage]);
  };

  const handleDelete = () => {
    setDeleteLoader(true);
    deleteBanner(selectedPicture?.id)
      .then(({ data: { status, message } }) => {
        setOpen(false);
        if (status) {
          toastMessage("success", message);
          getAllBanners(false, activeTab === 1);
        } else {
          toastMessage("error", message);
        }
        setDeleteLoader(false);
      })
      .catch((err) => {
        setOpen(false);
        toastMessage("error", err?.response?.data?.message);
        setDeleteLoader(false);
      });
  };

  useEffect(() => {
    getAllBanners(true, activeTab === 1);
  }, [activeTab]);

  useBreadcrumbs({
    title: "App Banners",
    path: routeConstant.banners.path,
  });

  const bannerSize =
    activeTab === 1 ? BANNER_SIZES.welcome : BANNER_SIZES.home;

  const handleTabChange =(event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <div>
      <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Home Screen" sx={{ textTransform: "none" }} />
          <Tab label="Welcome Screen" sx={{ textTransform: "none" }} />
        </Tabs>
      </Box>

      <div className={styles.headingContainer}>
        <label className={styles.title}>
          {activeTab === 0
            ? "App Home Screen Banners"
            : "App Welcome Screen Banners"}
        </label>
        {/* Opens the same file picker as the "+" tile below. */}
        <label htmlFor="imageInput" className={styles.addNewBtn}>
          Add New Banner
        </label>
      </div>
      <div className={styles.subHeading}>
        Banners should be {bannerSize.width} × {bannerSize.height} pixels so
        they look sharp and load quickly.
      </div>

      <div
        className={`${styles.imageContainer} ${
          activeTab === 1 ? styles.welcome : ""
        }`}
      >
        {loader ? (
          <>
            {Array.from(Array(3).keys()).map((_, index) => (
              <React.Fragment key={index}>
                <Skeleton variant="rectangular" className={styles.loader} />
              </React.Fragment>
            ))}
          </>
        ) : (
          allBanners?.map((item: any, index: any) => (
            <div key={index} style={{ position: "relative" }}>
              <img src={item.image} className={styles.preview} alt="Preview" />

              <HighlightOffIcon
                sx={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  cursor: "pointer",
                  color: "#9A469B",
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                }}
                onClick={() => handleClickOpen(item)}
              />
            </div>
          ))
        )}

        <ImageUploader
          handleImageUpload={handleImageUpload}
          getAllBanners={getAllBanners}
          isMobile={activeTab === 1}
        />
      </div>
      <Confirmation
        open={open}
        handleClose={handleClose}
        handleClick={handleDelete}
        text="Are you sure, you want to delete this banner?"
      />
      {deleteLoader && <Loader />}
    </div>
  );
};

export default Banners;

interface ImageUploaderProps {
  handleImageUpload: (newImage: Image) => void;
  getAllBanners: any;
  isMobile: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  handleImageUpload,
  getAllBanners,
  isMobile,
}) => {
  /** The picked file as a data URL, while the crop dialog is open on it. */
  const [cropSource, setCropSource] = useState<string>("");
  const [addLoader, setAddLoader] = useState<boolean>(false);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again still fires onChange.
    event.target.value = "";
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) setCropSource(reader.result.toString());
    };
    reader.readAsDataURL(file);
  };

  const bannerSize = isMobile ? BANNER_SIZES.welcome : BANNER_SIZES.home;

  /** Uploads the cropped banner, which is always bannerSize's shape. */
  const uploadBanner = (file: File) => {
    if (!file) return;

    let resp: any = {
      image: file,
      type: "1",
      isMobile: isMobile ? "true" : "false",
    };
    let formBody = new FormData();
    Object.keys(resp).forEach((key) => {
      formBody.append(key, resp[key]);
    });
    setAddLoader(true);

    addBanner(formBody)
      .then(({ data: { status, message } }) => {
        setAddLoader(false);
        if (status) {
          toastMessage("success", message);
        } else {
          toastMessage("error", message);
        }
        getAllBanners(false, isMobile);
      })
      .catch((err) => {
        toastMessage("error", err.response?.data?.message);
        setAddLoader(false);
      });
  };

  return (
    <div>
      <input
        type="file"
        id="imageInput"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageChange}
      />
      <label htmlFor="imageInput">
        <div className={styles.singleContainer}>
          <AddIcon style={{ color: "#ADB5BD" }} />
        </div>
      </label>
      <CropImage
        open={Boolean(cropSource)}
        handleClose={() => setCropSource("")}
        image={cropSource}
        setImage={setCropSource}
        handleImage={uploadBanner}
        aspectRatio={bannerSize.width / bannerSize.height}
        label={`Adjust Banner (${bannerSize.width} × ${bannerSize.height})`}
      />
      {addLoader && <Loader />}
    </div>
  );
};
